/**
 * Builds an optimized image URL that routes through the CloudFront
 * image optimizer Lambda (/_img/* path).
 *
 * Both dev (dev-media.nexmail.ai) and prod (media.nexmail.ai) use the same
 * CloudFront-fronted Lambda optimizer with CORS configured for our web
 * origins, so we always rewrite — there is no dev bypass.
 *
 * External URLs (Shopify CDN, etc.) are signed/proxied server-side at
 * compile time (see api/src/shared/utils/optimized-image-url.ts). The web
 * never holds the HMAC key, so external URLs are passed through here
 * unchanged on the assumption they were already proxied upstream.
 *
 * @example
 *   optimizedImageUrl('https://media.nexmail.ai/org/client/photo.jpg', { w: 800 })
 *   // → 'https://media.nexmail.ai/_img/org/client/photo.jpg?w=800&q=80&f=webp'
 */

const MEDIA_HOST_PATTERNS = [
  /^media\.nexmail\.ai$/,
  /^dev-media\.nexmail\.ai$/,
  /\.cloudfront\.net$/,
  /^nexmail.*\.s3\..+\.amazonaws\.com$/,
];

export interface ImageOptimizeParams {
  /** Target width in pixels (max 2048) */
  w: number;
  /** Target height in pixels (optional, preserves aspect ratio if omitted) */
  h?: number;
  /** Quality 1-100 (default 80) */
  q?: number;
  /** Output format (default 'webp') */
  f?: 'webp' | 'jpeg' | 'png' | 'avif';
}

export function optimizedImageUrl(
  src: string,
  params: ImageOptimizeParams,
): string {
  try {
    const url = new URL(src);

    // Already optimized — don't double-rewrite (e.g. server already signed).
    if (url.pathname.startsWith('/_img/') || url.pathname.startsWith('/_proxy/')) return src;

    const isOurMedia = MEDIA_HOST_PATTERNS.some((p) => p.test(url.hostname));
    if (!isOurMedia) return src;

    // Rewrite: /path/to/img.jpg → /_img/path/to/img.jpg?w=800&q=80&f=webp
    const optimizedUrl = new URL(src);
    optimizedUrl.pathname = `/_img${url.pathname}`;
    optimizedUrl.searchParams.set('w', String(Math.min(params.w, 2048)));
    if (params.h) optimizedUrl.searchParams.set('h', String(Math.min(params.h, 2048)));
    optimizedUrl.searchParams.set('q', String(params.q ?? 80));
    optimizedUrl.searchParams.set('f', params.f ?? 'webp');

    return optimizedUrl.toString();
  } catch {
    return src;
  }
}

/**
 * Returns the optimal image width to request based on the display size.
 * Accounts for devicePixelRatio and rounds up to the nearest step
 * to maximize cache hit rate (fewer unique URLs = more cache hits).
 */
export function optimalWidth(displayWidth: number): number {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const targetPx = displayWidth * dpr;

  // Round up to nearest step for better cache hits
  const steps = [200, 400, 600, 800, 1200, 1600, 2048];
  return steps.find((s) => s >= targetPx) ?? 2048;
}
